import { createPublicClient, http, formatEther, formatUnits } from "viem";
import * as viemChains from "viem/chains";
import { useOnChainBalanceOverrides, OverrideEntry } from "./on-chain-balance-overrides";
import { mobileDebugLog } from "./mobile-debug-log";
import { bumpRenderTick } from "./render-tick-store";
import { logDiagnostic } from "./silent-diagnostic";

// Chain ID to viem chain mapping (matches send-modal.tsx pattern)
const VIEM_CHAINS: Record<number, viemChains.Chain> = {
  1: viemChains.mainnet,
  56: viemChains.bsc,
  137: viemChains.polygon,
  42161: viemChains.arbitrum,
  10: viemChains.optimism,
  43114: viemChains.avalanche,
  8453: viemChains.base,
  324: viemChains.zksync,
  59144: viemChains.linea,
  534352: viemChains.scroll,
  81457: viemChains.blast,
  250: viemChains.fantom,
  100: viemChains.gnosis,
  5000: viemChains.mantle,
};

// RPC URLs via the app's proxy (matches existing patterns)
const getRpcUrl = (chainId: number): string => {
  const chainMap: Record<number, string> = {
    1: "/api/rpc?chain=ethereum",
    56: "/api/rpc?chain=bsc",
    137: "/api/rpc?chain=polygon",
    42161: "/api/rpc?chain=arbitrum",
    10: "/api/rpc?chain=optimism",
    43114: "/api/rpc?chain=avalanche",
    8453: "/api/rpc?chain=base",
    324: "/api/rpc?chain=zksync",
    59144: "/api/rpc?chain=linea",
    534352: "/api/rpc?chain=scroll",
    81457: "/api/rpc?chain=blast",
    250: "/api/rpc?chain=fantom",
    100: "/api/rpc?chain=gnosis",
    5000: "/api/rpc?chain=mantle",
  };
  return chainMap[chainId] || "/api/rpc?chain=ethereum";
};

export interface TokenReadInfo {
  address: string;      // "" for native
  chainId: number;
  symbol: string;
  decimals: number;
  isNative: boolean;
}

// ERC20 balanceOf ABI fragment
const balanceOfABI = [
  {
    inputs: [{ name: "account", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
] as const;

/**
 * Read on-chain balance for a single token and store as override.
 * Fire-and-forget safe — logs errors but doesn't throw.
 */
export async function readAndStoreOverride(
  userAddress: string,
  token: TokenReadInfo
): Promise<void> {
  // Silent diagnostic — pure localStorage, no re-renders
  logDiagnostic({ event: "read-start", symbol: token.symbol, chainId: token.chainId, isNative: token.isNative, userAddr: userAddress?.slice(0, 8) });

  const chain = VIEM_CHAINS[token.chainId];
  if (!chain) {
    return;
  }

  bumpRenderTick();
  mobileDebugLog("OVR-START", `${token.symbol}`, {
    chainId: token.chainId,
    isNative: token.isNative,
    userAddr: userAddress?.slice(0, 8) + "...",
  });

  const rpcUrl = getRpcUrl(token.chainId);
  const client = createPublicClient({
    chain,
    transport: http(rpcUrl),
  });

  try {
    let rawBalance: bigint;
    let formattedBalance: string;

    if (token.isNative || !token.address || token.address === "") {
      // Native token balance
      rawBalance = await client.getBalance({
        address: userAddress as `0x${string}`,
      });
      formattedBalance = formatEther(rawBalance);
    } else {
      // ERC20 token balance
      rawBalance = await client.readContract({
        address: token.address as `0x${string}`,
        abi: balanceOfABI,
        functionName: "balanceOf",
        args: [userAddress as `0x${string}`],
      });
      formattedBalance = formatUnits(rawBalance, token.decimals);
    }

    // Normalize native token addresses: callers may pass "" or zeroAddress depending on source
    // (Para returns "", wagmi returns zeroAddress). Always store under zeroAddress for consistency
    // with the lookup side in use-token-balances.tsx.
    const ZERO_ADDR = "0x0000000000000000000000000000000000000000";
    const rawAddr = (token.address || "").toLowerCase();
    const normalizedAddr = (rawAddr === "" || rawAddr === ZERO_ADDR) ? ZERO_ADDR : rawAddr;
    const key = `${token.chainId}-${normalizedAddr}`;

    // Silent diagnostic — successful read
    logDiagnostic({ event: "read-success", symbol: token.symbol, formatted: formattedBalance, raw: rawBalance.toString() });

    bumpRenderTick();
    mobileDebugLog("OVR-READ", `${token.symbol}=${formattedBalance}`, {
      raw: rawBalance.toString(),
    });

    const entry: OverrideEntry = {
      balance: formattedBalance,
      lastReadAt: Date.now(),
      chainId: token.chainId,
      tokenAddress: normalizedAddr,
      symbol: token.symbol,
      decimals: token.decimals,
    };

    useOnChainBalanceOverrides.getState().setOverride(key, entry);

    // Silent diagnostic — read back from store to verify it was set
    logDiagnostic({ event: "store-set", symbol: token.symbol, key, valueInStore: JSON.stringify(useOnChainBalanceOverrides.getState().overrides[key] || null) });

    bumpRenderTick();
    mobileDebugLog("OVR-STORED", `${token.symbol}`, { key });
  } catch (e) {
    // Silent diagnostic — error case
    logDiagnostic({ event: "read-error", symbol: token.symbol, error: String(e).slice(0, 200) });

    bumpRenderTick();
    mobileDebugLog("OVR-ERROR", `${token.symbol}`, {
      error: String(e).slice(0, 100),
    });
    // Do NOT set an override on failure — fall back to Para
  }
}

/**
 * Re-read all existing overrides with fresh on-chain data.
 * USD values are computed at injection time using Para's current prices.
 */
export async function refreshAllOverrides(userAddress: string): Promise<void> {
  const { overrides } = useOnChainBalanceOverrides.getState();
  const entries = Object.values(overrides);

  if (entries.length === 0) {
    return;
  }

  const ZERO_ADDR = "0x0000000000000000000000000000000000000000";
  const promises = entries.map((entry) =>
    readAndStoreOverride(userAddress, {
      address: entry.tokenAddress,
      chainId: entry.chainId,
      symbol: entry.symbol,
      decimals: entry.decimals,
      isNative: entry.tokenAddress === "" || entry.tokenAddress === ZERO_ADDR,
    })
  );

  await Promise.all(promises);
}
